import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, MapPin, Mountain, Truck, PackageOpen } from 'lucide-react'
import Drawer from './Drawer'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { LoadingState, ErrorState } from './ViewState'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'

const EMPTY = { name: '', type: 'load', mine_id: '', pit: '', lat: '', lon: '', polygon: null }

const TYPE_VALUES = ['load', 'dump']

export default function AdminZones() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { mines } = useWS()
  const typeLabel = (ty) => t(ty === 'dump' ? 'admin:zones.dump' : 'admin:zones.load')
  const [zones, setZones] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [listError, setListError] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const mineName = (id) => mines.find(m => String(m.id) === String(id))?.name || null

  const load = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/zones')
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setZones(Array.isArray(data) ? data : [])
      setListError(null)
    } catch (err) {
      // Erro de carga NÃO é lista vazia: sem isto a tela convidava a cadastrar zonas
      // que já existem.
      setZones([])
      setListError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(null); setOpen(true) }
  const openEdit = (z) => {
    setEditingId(z.id)
    setForm({
      name: z.name || '',
      type: z.type === 'dump' ? 'dump' : 'load',
      mine_id: z.mine_id ?? '',
      pit: z.pit || '',
      lat: z.lat ?? '',
      lon: z.lon ?? '',
      polygon: z.polygon ?? null,
    })
    setError(null); setOpen(true)
  }

  const save = async (e) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      const body = {
        name: form.name.trim(),
        type: form.type,
        mine_id: form.mine_id === '' ? null : Number(form.mine_id),
        pit: form.pit.trim() || null,
        lat: form.lat === '' ? null : Number(form.lat),
        lon: form.lon === '' ? null : Number(form.lon),
        polygon: form.polygon ?? null,
      }
      const url = editingId ? `/api/zones/${editingId}` : '/api/zones'
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) throw await apiError(res)
      setOpen(false); load()
    } catch (err) { setError(err.message) } finally { setSaving(false) }
  }

  const remove = (z) => ask({
    title: t('common:actions.delete'),
    message: t('admin:zones.delete_confirm', { name: z.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/zones/${z.id}`); load() },
  })

  const groups = [
    { type: 'load', label: t('admin:zones.load'), icon: PackageOpen, items: zones.filter(z => z.type !== 'dump') },
    { type: 'dump', label: t('admin:zones.dump'), icon: Truck, items: zones.filter(z => z.type === 'dump') },
  ]

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('admin:zones.title')}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:zones.count', { count: zones.length })}</p>
        </div>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:zones.new')}
        </button>
      </div>

      {loading ? (
        <LoadingState className="h-48" />
      ) : listError ? (
        <ErrorState message={listError} onRetry={load} className="h-48" />
      ) : zones.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:zones.empty')}</span>
        </button>
      ) : (
        <div className="space-y-6">
          {groups.map(g => {
            if (g.items.length === 0) return null
            const GroupIcon = g.icon
            return (
              <div key={g.type}>
                <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <GroupIcon size={15} className={g.type === 'dump' ? 'text-amber-500' : 'text-brand-600'} />
                  {g.label}
                  <span className="text-slate-400 dark:text-slate-500">· {g.items.length}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {g.items.map(z => {
                    const mn = mineName(z.mine_id)
                    const isDump = z.type === 'dump'
                    return (
                      <div key={z.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{z.name}</p>
                            <span className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${isDump ? 'bg-amber-100 text-amber-700' : 'bg-brand/10 text-brand-700'}`}>
                              {typeLabel(z.type)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button disabled={!mayEdit} onClick={() => openEdit(z)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                            <button disabled={!mayDelete} onClick={() => remove(z)} title={!mayDelete ? t(reasonDelete) : t('common:actions.delete')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                          {(mn || z.pit) && (
                            <p className="flex items-center gap-1.5">
                              <Mountain size={12} className="text-slate-400 dark:text-slate-500" />
                              <span className="truncate">{mn || t('common:scope.no_mine')}{z.pit ? ` · ${z.pit}` : ''}</span>
                            </p>
                          )}
                          {z.lat != null && z.lon != null && (
                            <p className="flex items-center gap-1.5">
                              <MapPin size={12} className="text-slate-400 dark:text-slate-500" />
                              <span className="tabular-nums">{Number(z.lat).toFixed(4)}, {Number(z.lon).toFixed(4)}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? t('admin:zones.edit') : t('admin:zones.new')}
        subtitle={t('admin:zones.subtitle')}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
            <button type="submit" form="zone-form" disabled={saving} className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
              {saving ? t('admin:actions.saving') : t('common:actions.save')}
            </button>
          </div>
        }
      >
        <form id="zone-form" onSubmit={save} className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:zones.fields.name')}</span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('admin:zones.fields.name_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:zones.fields.type')}</span>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
              {TYPE_VALUES.map(ty => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.mine')} <span className="text-slate-300 dark:text-slate-600">{t('admin:zones.fields.optional')}</span></span>
            <select value={form.mine_id} onChange={e => setForm({ ...form, mine_id: e.target.value })} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
              <option value="">{t('admin:zones.fields.no_mine_option')}</option>
              {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.pit')} <span className="text-slate-300 dark:text-slate-600">{t('admin:zones.fields.optional')}</span></span>
            <input value={form.pit} onChange={e => setForm({ ...form, pit: e.target.value })} placeholder={t('admin:zones.fields.pit_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:zones.fields.lat')}</span>
              <input type="number" step="any" value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} placeholder={t('admin:zones.fields.lat_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:zones.fields.lon')}</span>
              <input type="number" step="any" value={form.lon} onChange={e => setForm({ ...form, lon: e.target.value })} placeholder={t('admin:zones.fields.lon_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 tabular-nums focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
            </label>
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </form>
      </Drawer>
      {confirmDialog}
    </div>
  )
}
